import { useEffect, useState, RefObject } from 'react';

interface UseIntersectionObserverOptions {
  ref: RefObject<HTMLElement>;
  threshold?: number;
  root?: Element | null;
  rootMargin?: string;
  enabled?: boolean;
}

/**
 * Hook для отслеживания видимости элемента
 * Полезно для остановки анимаций и загрузки данных когда элемент не виден
 */
export const useIntersectionObserver = ({
  ref,
  threshold = 0.1,
  root = null,
  rootMargin = '0px',
  enabled = true
}: UseIntersectionObserverOptions) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);

  useEffect(() => {
    if (!enabled || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setIsIntersecting(isVisible);
        if (isVisible && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        threshold,
        root,
        rootMargin
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [threshold, root, rootMargin, enabled, hasIntersected, ref]);

  return {
    isIntersecting,
    hasIntersected
  };
};

